import { Assets, Spritesheet, Texture } from 'pixi.js';
import { AssetLoader } from '../../../assetpacker/AssetLoader';

/**
 * Base class for all atlases
 * Contains common logic for loading, unloading and getting textures
 */
export abstract class BaseAtlas {
    protected static _spritesheet: Spritesheet | null = null;
    protected static _loaded: boolean = false;
    protected static _loading: Promise<void> | null = null;

    protected static readonly ATLAS_PATH: string;
    protected static readonly TEXTURES: readonly string[];

    public static isLoaded(): boolean {
        return this._loaded;
    }

    public static async load(): Promise<void> {
        if (this._loaded && this._spritesheet) {
            return;
        }

        if (this._loading) {
            return this._loading;
        }

        this._loading = (async () => {
            try {
                const loader = AssetLoader.getInstance();
                const pathParts = this.ATLAS_PATH.split('/');
                const atlasName = pathParts[pathParts.length - 1];
                const atlasDir = pathParts.slice(0, -1).join('/');
                
                const spritesheet = await loader.loadAtlas(atlasDir, atlasName);
                
                if (spritesheet) {
                    this._spritesheet = spritesheet;
                    this._loaded = true;
                } else {
                    throw new Error(`Failed to load atlas "${this.ATLAS_PATH}"`);
                }
            } finally {
                this._loading = null;
            }
        })();

        return this._loading;
    }

    public static unload(): void {
        if (this._spritesheet) {
            this._spritesheet.destroy(false);
            this._spritesheet = null;
        }
        this._loaded = false;
        
        const loader = AssetLoader.getInstance();
        const pathParts = this.ATLAS_PATH.split('/');
        const atlasName = pathParts[pathParts.length - 1];
        const atlasDir = pathParts.slice(0, -1).join('/');
        
        Assets.cache.remove(this.ATLAS_PATH);
        loader.removeAtlasFromTracking(atlasDir, atlasName);
    }

    public static async getTextureByName(name: string): Promise<Texture> {
        const sheet = await this.ensureSpritesheetAsync();
        const textures = sheet.textures as Record<string, Texture>;
        const texture = textures[name];

        if (!texture) {
            throw new Error(`Texture "${name}" not found in atlas "${this.ATLAS_PATH}"`);
        }

        return texture;
    }
    
    public static getTextureByNameSync(name: string): Texture {
        const sheet = this.ensureSpritesheet();
        const textures = sheet.textures as Record<string, Texture>;
        const texture = textures[name];

        if (!texture) {
            throw new Error(`Texture "${name}" not found in atlas "${this.ATLAS_PATH}"`);
        }

        return texture;
    }

    public static async getAllTextures(): Promise<Texture[]> {
        const sheet = await this.ensureSpritesheetAsync();
        return this.TEXTURES.map(name => {
            const textures = sheet.textures as Record<string, Texture>;
            return textures[name];
        }).filter(t => t !== undefined);
    }
    
    public static getAllTexturesSync(): Texture[] {
        const sheet = this.ensureSpritesheet();
        return this.TEXTURES.map(name => {
            const textures = sheet.textures as Record<string, Texture>;
            return textures[name];
        }).filter(t => t !== undefined);
    }

    public static getSpritesheet(): Spritesheet {
        return this.ensureSpritesheet();
    }

    public static clearCache(): void {
        this._spritesheet = null;
        this._loaded = false;
        this._loading = null;
    }

    public static registerSpritesheet(sheet: Spritesheet): void {
        this._spritesheet = sheet;
        this._loaded = true;
    }

    protected static async ensureSpritesheetAsync(): Promise<Spritesheet> {
        if (this._spritesheet) {
            return this._spritesheet;
        }

        console.log(`⏳ Auto-loading atlas: ${this.ATLAS_PATH}`);
        await this.load();
        
        if (!this._spritesheet) {
            throw new Error(`Failed to load atlas "${this.ATLAS_PATH}"`);
        }
        
        return this._spritesheet;
    }

    protected static ensureSpritesheet(): Spritesheet {
        if (this._spritesheet) {
            return this._spritesheet;
        }

        const resource = Assets.get(this.ATLAS_PATH);

        if (resource instanceof Spritesheet) {
            this._spritesheet = resource;
            this._loaded = true;
            return resource;
        }

        if (resource && typeof resource === 'object' && 'textures' in resource) {
            const sheet = resource as Spritesheet;
            this._spritesheet = sheet;
            this._loaded = true;
            return sheet;
        }

        const className = this.name;
        throw new Error(
            `Atlas "${this.ATLAS_PATH}" not loaded.\n` +
            `Use one of the methods:\n` +
            `  1. await ${className}.load()\n` +
            `  2. await ${className}.getTextureByName(name) - with auto-loading\n` +
            `  3. Add atlas to preloadAssets when initializing AssetLoader`
        );
    }
}
